import "./App.css";
import { useEffect, useState, createContext, useContext } from "react";
import { BrowserRouter, Routes, Route, Navigate } from "react-router-dom";
import { Toaster } from "./components/ui/sonner";
import { AuthProvider, useAuth } from "./context/AuthContext";
import { CartProvider } from "./context/CartContext";
import { FavoritesProvider } from "./context/FavoritesContext";
import { LanguageProvider, useLang } from "./context/LanguageContext";
import Header from "./components/Header";
import { Clock } from "./components/Clock";
import { useLocation } from "react-router-dom";
import LoginPage from "./pages/LoginPage";
import LandingPage from "./pages/LandingPage";
import CatalogPage from "./pages/CatalogPage";
import CartPage from "./pages/CartPage";
import AdminPage from "./pages/AdminPage";

export const ThemeContext = createContext(null);
export const useTheme = () => useContext(ThemeContext);

function Protected({ children, adminOnly, adminArea, noStaff }) {
  const { user, loading } = useAuth();
  const { t } = useLang();
  if (loading) return <div className="min-h-screen flex items-center justify-center text-muted-foreground">{t("app.loading")}</div>;
  if (!user) return <Navigate to="/login" replace />;
  if (adminOnly && user.role !== "admin") return <Navigate to="/catalogo" replace />;
  if (adminArea && !["admin", "staff"].includes(user.role)) return <Navigate to="/catalogo" replace />;
  if (noStaff && user.role === "staff") return <Navigate to="/catalogo" replace />;
  if (!adminOnly && !adminArea && user.role !== "admin" && user.status !== "approved") return <PendingScreen status={user.status} />;
  return children;
}

function PendingScreen({ status }) {
  const { logout } = useAuth();
  const { t } = useLang();
  return (
    <div className="min-h-[70vh] flex items-center justify-center px-4" data-testid="pending-approval-screen">
      <div className="max-w-md text-center space-y-4 fade-up">
        <div className="text-5xl">{status === "rejected" ? "🚫" : "⏳"}</div>
        <h1 className="text-2xl font-bold">
          {status === "rejected" ? t("pending.rejectedTitle") : t("pending.pendingTitle")}
        </h1>
        <p className="text-muted-foreground">
          {status === "rejected" ? t("pending.rejectedBody") : t("pending.pendingBody")}
        </p>
        <button onClick={logout} className="text-sm underline text-muted-foreground" data-testid="pending-logout-button">{t("pending.logout")}</button>
      </div>
    </div>
  );
}

function AppFooter() {
  const location = useLocation();
  if (location.pathname === "/login" || location.pathname === "/") return null;
  return (
    <footer className="border-t border-border mt-8 py-5 px-4" data-testid="app-footer">
      <div className="max-w-7xl mx-auto flex justify-center">
        <Clock className="text-muted-foreground" />
      </div>
    </footer>
  );
}

function AppShell() {
  return (
    <BrowserRouter>
      <Header />
      <Routes>
        <Route path="/login" element={<LoginPage />} />
        <Route path="/" element={<LandingPage />} />
        <Route path="/catalogo" element={<Protected><CatalogPage /></Protected>} />
        <Route path="/carrito" element={<Protected noStaff><CartPage /></Protected>} />
        <Route path="/admin" element={<Protected adminArea><AdminPage /></Protected>} />
        <Route path="*" element={<Navigate to="/" replace />} />
      </Routes>
      <AppFooter />
    </BrowserRouter>
  );
}

export default function App() {
  const [dark, setDark] = useState(() => localStorage.getItem("theme") === "dark");
  useEffect(() => {
    document.documentElement.classList.toggle("dark", dark);
    localStorage.setItem("theme", dark ? "dark" : "light");
  }, [dark]);

  return (
    <AuthProvider>
      <LanguageProvider>
        <CartProvider>
          <FavoritesProvider>
          <ThemeContext.Provider value={{ dark, toggle: () => setDark((d) => !d) }}>
            <div className="min-h-screen bg-background text-foreground">
              <AppShell />
              <Toaster richColors position="bottom-center" />
            </div>
          </ThemeContext.Provider>
          </FavoritesProvider>
        </CartProvider>
      </LanguageProvider>
    </AuthProvider>
  );
}
